import { createFeatureSelector, createSelector } from '@ngrx/store';

import { selectRouterState } from '@core/stores/router/router.selectors';
import { selectAccessibleSites, selectTenantsIsLoading } from '@core/stores/tenants/tenants.selectors';

import { ConnectorTypeStoreModelEnum, EvseStatusStoreModelEnum, SiteTypeStoreModelEnum } from '@domain/single-site/single-site.enums';

import {
  DepotOverviewCardViewModel,
  DepotVehicleOverviewCardViewModel,
  createDepotVehicleOverviewCardViewModel,
} from '@shared/components/depot-overview-card';
import { DepotOverviewChartViewModel } from '@shared/components/depot-overview-chart';
import { EVSE_STATUS_PROPERTIES_MAP } from '@shared/components/evse-status-icon';
import { getPathParamOfRoute } from '@shared/utils/router-state';

import { SingleSiteState, singleSiteFeatureKey } from './single-site.reducer';

export const selectSingleSiteSiteId = createSelector(selectRouterState, (state) => {
  return getPathParamOfRoute(state, 'siteId');
});

export const selectSingleSiteFeature = createFeatureSelector<SingleSiteState>(singleSiteFeatureKey);
export const selectAssetDetailsDialogWide = createSelector(selectSingleSiteFeature, (state) => state.assetDetailsDialogWide);

export const selectSiteData = createSelector(selectAccessibleSites, selectSingleSiteSiteId, (siteList, siteId) =>
  siteList?.find((site) => site.id === siteId)
);

export const selectVehicleOverview = createSelector(selectSingleSiteFeature, (state) => state.vehicleOverview);
export const selectVehicleOverviewIsLoading = createSelector(selectSingleSiteFeature, (state) => state.vehicleOverviewIsLoading);
export const selectVehicleOverviewError = createSelector(selectSingleSiteFeature, (state) => state.vehicleOverviewError);

export const selectEvseOverview = createSelector(selectSingleSiteFeature, (state) => state.evseOverview);
export const selectEvseOverviewIsLoading = createSelector(selectSingleSiteFeature, (state) => state.evseOverviewIsLoading);
export const selectEvseOverviewError = createSelector(selectSingleSiteFeature, (state) => state.evseOverviewError);

export const selectCurrentSiteData = createSelector(selectSiteData, (siteData) => siteData ?? null);

export const selectTelemetryFeaturesEnabled = createSelector(selectCurrentSiteData, (siteData) => {
  return siteData?.features?.telemetryFeaturesEnabled === true;
});

export const selectGridViewEnabled = createSelector(selectCurrentSiteData, (siteData) => {
  return siteData?.features?.gridViewEnabled === true;
});

export const selectKioskV2EvseCardsEnabled = createSelector(selectCurrentSiteData, (siteData) => {
  return siteData?.features?.kioskV2EvseCardsEnabled === true;
});

export const selectKioskEvseNameOnlyEnabled = createSelector(selectCurrentSiteData, (siteData) => {
  return siteData?.features?.kioskEvseNameOnlyEnabled === true;
});

export const selectCurrentSiteName = createSelector(selectCurrentSiteData, (siteData) => {
  return siteData?.name ?? '';
});

export const selectCurrentSiteTimeZone = createSelector(selectCurrentSiteData, (siteData) => {
  return siteData?.timeZone ?? '';
});

export const selectDepotVehicleOverviewCardViewModel = createSelector(
  selectVehicleOverview,
  (vehicleOverview): DepotVehicleOverviewCardViewModel | undefined => {
    if (!vehicleOverview) {
      return;
    }

    return createDepotVehicleOverviewCardViewModel(vehicleOverview.statusData);
  }
);

export const selectDepotEvseOverviewCardViewModel = createSelector(
  selectEvseOverview,
  (evseOverview): DepotOverviewCardViewModel | undefined => {
    if (!evseOverview) {
      return;
    }

    const statusData = evseOverview.data;

    const chartVm: DepotOverviewChartViewModel<EvseStatusStoreModelEnum> = {
      data: {
        [EvseStatusStoreModelEnum.Available]: statusData[EvseStatusStoreModelEnum.Available] || 0,
        [EvseStatusStoreModelEnum.Queued]: statusData[EvseStatusStoreModelEnum.Queued] || 0,
        [EvseStatusStoreModelEnum.Charging]: statusData[EvseStatusStoreModelEnum.Charging] || 0,
        [EvseStatusStoreModelEnum.Faulted]: statusData[EvseStatusStoreModelEnum.Faulted] || 0,
        [EvseStatusStoreModelEnum.Maintenance]: statusData[EvseStatusStoreModelEnum.Maintenance] || 0,
      },
      propertiesMap: EVSE_STATUS_PROPERTIES_MAP,
    };

    if (statusData[EvseStatusStoreModelEnum.Unknown]) {
      chartVm.data[EvseStatusStoreModelEnum.Unknown] = statusData[EvseStatusStoreModelEnum.Unknown];
    }

    return {
      chartVm,
      type: SiteTypeStoreModelEnum.Public,
      mostCommonConnectorType: evseOverview.dominantConnectorType || ConnectorTypeStoreModelEnum.Ccs2,
    };
  }
);

export const selectDepotOverviewCardViewModel = createSelector(
  selectCurrentSiteData,
  selectDepotVehicleOverviewCardViewModel,
  selectDepotEvseOverviewCardViewModel,
  (currentSiteData, depotVehicleOverviewCardViewModel, depotEvseOverviewCardViewModel): DepotOverviewCardViewModel | undefined => {
    if (!currentSiteData) {
      return;
    }

    return currentSiteData.siteType === SiteTypeStoreModelEnum.Fleet ? depotVehicleOverviewCardViewModel : depotEvseOverviewCardViewModel;
  }
);

export const selectDepotOverviewCardIsLoading = createSelector(
  selectVehicleOverviewIsLoading,
  selectEvseOverviewIsLoading,
  selectTenantsIsLoading,
  (vehicleOverviewIsLoading, evseOverviewIsLoading, siteListIsLoading) =>
    vehicleOverviewIsLoading || evseOverviewIsLoading || siteListIsLoading
);
